import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { type Order } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import { getLocalStorageOrderId } from '@order/features-ordering';
import {
  type FetchStatePostOrderParams,
  type FetchStatePostOrderResponse,
} from '@order/features-ordering';

import { BagPostOrderDocument, type BagPostOrderQuery } from '../../GraphQL/BagPostOrder.generated';
import { prepareCart } from '../../utils';

/**
 * With frontend intent confirmation, the backend will place the order on its own.
 * To ensure the cart is cleared, we'll fetch the order status.
 */
export const usePostOrderFetch = () => {
  const client = useClient();

  const fetchStatePostOrder = useMemo(
    () =>
      fromPromise<FetchStatePostOrderResponse, FetchStatePostOrderParams>(async ({ input }) => {
        const { orderId, orderType } = await getOrderIdAndType(input);
        const bagPostOrderQuery = client.query<BagPostOrderQuery>;

        if (!orderId) throw new Error('Order ID is required');
        if (orderType !== OrderTypeValues.GravyOrder) throw new Error('Must be gravy order');

        const response = await bagPostOrderQuery(
          BagPostOrderDocument,
          { id: orderId },
          { requestPolicy: 'network-only' },
        ).toPromise();

        const maybeOrderStatus = response.data?.orderStatus;

        if (maybeOrderStatus?.__typename === 'InvalidOrder') {
          throw new Error('Invalid order');
        }

        const order = maybeOrderStatus?.order;

        if (!order) {
          throw new Error('Order not found');
        }

        return {
          order,
          cart: prepareCart(order as Partial<Order>),
        };
      }),
    [client.query],
  );

  return { fetchStatePostOrder };
};

async function getOrderIdAndType(input?: FetchStatePostOrderParams) {
  if (input?.orderId) {
    return { orderId: input.orderId, orderType: OrderTypeValues.GravyOrder };
  }

  return await getLocalStorageOrderId();
}
